import React, { useState, useEffect } from "react";
import Layout from "~/components/Layout";
import * as S from "./styles";
import themes from "~/themes";
import assets from "~/assets";
import { useLocation, useHistory } from "react-router-dom";
import Endpoints from "~/services/endpoints";
import useApi, { ApiMethod } from "~/hooks/useApi";
import ModalOneButton from "~/components/Modals/OneButtonPopup";
import { Content } from "../../styles";
import InvoiceSummaryCard from "../../components/InvoiceSummaryCard";
import InfoBoxesRow from "../../components/InfoBoxesRow";
import QrCodePix from "../../components/QrCodePix";
import FooterActions from "../../components/FooterActions";
import InstallmentBox from "../../components/InstallmentBox/index.js";

const InputCPF = () => {
  const { state } = useLocation();
  const history = useHistory();
  const data = state.data;
  const lastParcel = data?.parcelamento?.[data.parcelamento.length - 1];
  const isInvoicePaid = ["PAGA", "FECHADA_PAGA", "PAGA_COBMAIS"].includes(data?.status);
  const [isLoading, setIsLoading] = useState(false);
  const [modalIsOpenFeed, setModalIsOpenFeed] = useState(false);
  const [modalText, setModalText] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [base64QrCode, setBase64QrCode] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const invoiceId = data.id_fatura;

  useEffect(() => {
    const timeout = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);

    return () => clearTimeout(timeout);
  }, [useLocation().pathname]);

  useEffect(() => {
    if (invoiceId && !isInvoicePaid) {
      getQrCode();
    }
  }, [invoiceId, isInvoicePaid]);

  const POSTUltimaFaturaPdf = useApi(
    Endpoints.relatorio.obterUltimaFaturaPorCpfDataNascimento,
    ApiMethod.POST
  );

  const GetQrCode = useApi(
    Endpoints.charge.getGenerateQrCode,
    ApiMethod.POST
  );

  const isQrLoading = GetQrCode.loading;
  const isQrReady = !!base64QrCode?.base64 && !!base64QrCode?.emv;

  const formatBirthDateToApi = (date) => {
    if (!date) return "";
    const [day, month, year] = date.split("/");
    if (!day || !month || !year) return "";
    return `${year}-${month}-${day}`;
  };

  const downloadInvoice = (base64) => {
    const binary = atob(base64.replace(/\s/g, ""));
    const buffer = new ArrayBuffer(binary.length);
    const view = new Uint8Array(buffer);

    for (let i = 0; i < binary.length; i += 1) {
      view[i] = binary.charCodeAt(i);
    }

    const blob = new Blob([view], { type: "application/pdf" });
    const fileURL = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = fileURL;
    link.download = "fatura.pdf";
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(fileURL);
  };

  const handleCopyPixCode = () => {
    if (navigator?.clipboard?.writeText && base64QrCode?.emv) {
      navigator.clipboard.writeText(base64QrCode.emv);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setIsLoading(true);

      const payload = {
        cpf: data.cpf,
        dataNascimento: formatBirthDateToApi(data.dataNascimento),
      };
      const response = await POSTUltimaFaturaPdf.callApi(payload);

      if (response.status >= 200 && response.status < 300) {
        const base64Pdf =
          response.data?.documentFatura ||
          response.data?.pdfBase64 ||
          response.data?.documento;

        if (!base64Pdf) {
          throw new Error("PDF não encontrado");
        }

        downloadInvoice(base64Pdf);
      } else {
        throw new Error(response.data?.mensagem || "Erro ao baixar PDF");
      }
    } catch (error) {
      setModalText(error.message || "Erro ao baixar PDF");
      setModalIsOpenFeed(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getQrCode = async () => {
    try {
      const payload = {
        idFatura: data.id_fatura,
        valor: data.saldoFaturaAtual,
        cpf: data.cpf,
      };
      const response = await GetQrCode.callApi(payload);
      if (response.status >= 200 && response.status <= 300) {
        setBase64QrCode(response.data);
      }
      else {
        setShowPopup(true);
        setErrorMessage(response.mensagem || "Não conseguimos carregar seu QrCode");
      }

    } catch (err) {
      setShowPopup(true);
      setErrorMessage(err.message || "Não conseguimos carregar seu QrCode");
    }
  }

  const handleGoToDetails = () => {
    history.push("/Fatura-Detalhes", {
      data: data
    });
  };

  return (
    <Layout auth={false}>
      <ModalOneButton
        ishtml
        text={modalText}
        buttonText="Ok"
        icon={themes.components.icons.tick}
        visibility={modalIsOpenFeed}
        setVisibility={(bool) => setModalIsOpenFeed(bool)}
      />
      {isLoading && (
        <S.LoadingOverlay>
          <S.LoadingBox>
            <S.LoadingSpinner />
            <S.LoadingText>Baixando sua fatura...</S.LoadingText>
          </S.LoadingBox>
        </S.LoadingOverlay>
      )}
      <Content>
        <img src={themes.components.images.logoTeuCard} alt="Logo TeuCard" />

        <S.Title>Resumo de fatura</S.Title>
        <S.Subtitle>
          Confira os detalhes e opções de pagamento do teu cartão
        </S.Subtitle>

        <InvoiceSummaryCard data={data} />

        <InfoBoxesRow data={data} />

        {isInvoicePaid ? (
          <S.PaidStatusBox>
            <S.PaidStatusTitle>Tudo certo por aqui</S.PaidStatusTitle>
            <S.PaidStatusText>
              Tua fatura já consta como paga, você pode consultar os detalhes
              ou baixar o PDF pelos botões abaixo.
            </S.PaidStatusText>
          </S.PaidStatusBox>
        ) : (
          <S.BottomBoxesRow>
            <S.FixedBox>
              <QrCodePix
                loading={GetQrCode.loading}
                base64={base64QrCode?.base64}
                emv={base64QrCode?.emv}
                onCopy={handleCopyPixCode}
              />
            </S.FixedBox>

            <S.FixedBox>
              <InstallmentBox lastParcel={lastParcel} />
            </S.FixedBox>
          </S.BottomBoxesRow>
        )}

        <FooterActions
          onGoToDetails={handleGoToDetails}
          onDownloadPdf={handleDownloadPdf}
          icons={{
            details: assets.icons.openEyeCurrentColor,
            download: assets.icons.download,
          }}
          isLoadingDownload={isLoading}
        />
      </Content>
    </Layout>
  );
};

export default InputCPF;